import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../api'
import WizardShell from './wizard/WizardShell'
import StepBrowse from './wizard/StepBrowse'
import StepCanaux from './wizard/StepCanaux'
import { useCanaux, canauxChoisis, canauxTarifes, canauxInitiaux } from './canaux'
import StepPrice from './wizard/StepPrice'
import { calculerCanal, tvaVenteDe } from './prixCanal'
import StepTypeProduit from './wizard/StepTypeProduit'
import StepStockRecap from './wizard/StepStockRecap'
import StepProposeName from './wizard/StepProposeName'
import StepProposeCategory from './wizard/StepProposeCategory'
import StepProposePhoto from './wizard/StepProposePhoto'
import StepProposeUnite from './wizard/StepProposeUnite'
import { useTheme } from '../../theme'
import { useCachedFetch } from '../../lib/swr'

/**
 * Wizard « Ajouter un produit » — auto-contenu (fetch + cycle de vie interne).
 *
 * PARCOURS A REFAIT AU BANC D'ESSAI VISUEL (S68). Les cinq ecrans du prototype
 * v7.4 (`WizardOffre`), dans son ordre et sa forme :
 *   1. Quel produit ?      — recherche, rayons, liste
 *   2. Ou le vendre ?      — un interrupteur par canal (Tournee / Evenement / Atelier)
 *   3. Vos prix            — unite de vente + une carte par canal tarife, 2 modes, transparence
 *   4. Type de produit     — generique / ma marque
 *   5. Stock & recap       — quantite partagee + recapitulatif, « Creer mon offre »
 *
 * SOUS-PARCOURS « PROPOSER UN PRODUIT » ALIGNE SUR LE PROTOTYPE (S71, retour
 * Enzo : « pas exactement comme le prototype »). Quatre ecrans, une decision
 * chacun, sans progression : nom → rayon → photo (obligatoire) → « Vous le
 * vendez… ». Puis la proposition REJOINT le wizard comme un produit du
 * referentiel (canaux, prix, type, stock) et « Creer mon offre » cree les deux :
 * la reference (en validation Manrina) ET l'offre. La S68 s'arretait a une
 * demande de validation seule ; le producteur devait revenir creer l'offre.
 *
 * Etats : `mode` 'A' (catalogue) | 'B' (proposition en cours) ; la pile
 * `historyRef` porte le retour. L'anti-doublon de l'ecran « nom » peut
 * renvoyer sur le parcours A avec le produit trouve.
 */
function AddProductModal({
  tc, ts, tm, cardBg, inputBg,
  isOpen, onClose, onSuccess,
  modules,
}) {
  const { isWeb3 } = useTheme()
  // Catalogue des canaux : lieux du portail × activations de l'org (`./canaux.js`).
  const catalogue = useCanaux(modules)
  // --- Donnees catalogue ---
  // Rayons en cache SWR : la deuxieme ouverture n'attend pas le reseau.
  const { data: categoriesData } = useCachedFetch('producer/catalog/categories', () => api.producer.getCatalogCategories(), { enabled: isOpen })
  const categories = Array.isArray(categoriesData) ? categoriesData : []
  const [catalog, setCatalog] = useState([])
  // Squelette d'emblee : pas de liste vide ni de « Pas dans la liste ? » qui
  // clignote avant la premiere reponse.
  const [catLoading, setCatLoading] = useState(true)

  // --- Etat de navigation wizard ---
  const [mode, setMode] = useState('A') // 'A' (catalogue) | 'B' (propose)
  const [stepKey, setStepKey] = useState('browse')
  const [direction, setDirection] = useState('right')
  const historyRef = useRef([]) // pile pour Back

  // --- Etat partage parcours A ---
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState(null)
  // Produit du referentiel choisi OU proposition terminee
  // ({ proposition: true, nom, unit, category, photo, emoji: '🆕' }).
  const [selectedProduct, setSelectedProduct] = useState(null)
  // Canaux coches { TOURNEE: bool, MARCHE: bool, ... } — prototype : la Tournee
  // est cochee d'office ; ici le PREMIER canal ouvert. `null` = « pas encore
  // touche » : le defaut se calcule a la lecture, pour que l'arrivee (tardive)
  // de /public/modules ne remette pas le wizard a zero.
  const [canaux, setCanaux] = useState(null)
  const canauxEff = canaux ?? canauxInitiaux(catalogue)
  const toggleCanal = (code, on) => setCanaux((c) => ({ ...(c ?? canauxInitiaux(catalogue)), [code]: on }))
  // Prix par canal { CODE: { mode: 'net'|'client', val: '3,50' } }
  const [prixParCanal, setPrixParCanal] = useState({})
  // Unite de VENTE de l'offre (ADR-070) : `null` = celle du produit.
  const [offerUnit, setOfferUnit] = useState(null)
  const [exclusif, setExclusif] = useState(false)
  const [customName, setCustomName] = useState('')
  const [brandName, setBrandName] = useState('')
  const [stock, setStock] = useState('')

  // --- Etat parcours B (proposition en cours de saisie) ---
  const [proposeNom, setProposeNom] = useState('')
  const [proposeCategoryId, setProposeCategoryId] = useState('')
  const [proposePhoto, setProposePhoto] = useState(null)

  // --- Sauvegarde ---
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState(null)

  // --- Reset complet a l'ouverture ---
  useEffect(() => {
    if (!isOpen) return
    setMode('A')
    setStepKey('browse')
    setDirection('right')
    historyRef.current = []
    setSearch('')
    setCategoryId(null)
    setSelectedProduct(null)
    setCanaux(null)
    setPrixParCanal({})
    setOfferUnit(null)
    setExclusif(false)
    setCustomName(''); setBrandName('')
    setStock('')
    setProposeNom(''); setProposeCategoryId(''); setProposePhoto(null)
    setFormError(null)
  }, [isOpen])

  // --- Fetch catalog (debounced) selon search/categoryId ---
  useEffect(() => {
    if (!isOpen || stepKey !== 'browse') return
    setCatLoading(true)
    const timer = setTimeout(() => {
      api.producer.getCatalog(search, categoryId || undefined)
        .then((data) => setCatalog(Array.isArray(data) ? data : []))
        .catch(() => setCatalog([]))
        .finally(() => setCatLoading(false))
    }, 250)
    return () => clearTimeout(timer)
  }, [isOpen, stepKey, search, categoryId])

  // --- Etapes selon mode ---
  const stepsA = ['browse', 'canaux', 'price', 'type', 'stock']
  const stepsB = ['propose-name', 'propose-category', 'propose-photo', 'propose-unite']
  const proposeCategory = useMemo(
    () => categories.find((c) => c.id === proposeCategoryId) || null,
    [categories, proposeCategoryId],
  )

  const steps = mode === 'A' ? stepsA : stepsB
  const stepIndex = Math.max(0, steps.indexOf(stepKey))

  // --- Navigation helpers ---
  const goTo = (nextKey, dir = 'right') => {
    if (nextKey === stepKey) return
    historyRef.current.push(stepKey)
    setDirection(dir)
    setStepKey(nextKey)
    setFormError(null)
  }

  const goBack = () => {
    const prev = historyRef.current.pop()
    if (!prev) return
    // Revenir dans la proposition (depuis « Ou le vendre ? ») ou en sortir
    // (depuis son premier ecran) : le mode suit l'ecran ou l'on retourne.
    setMode(prev.startsWith('propose-') ? 'B' : 'A')
    setDirection('left')
    setStepKey(prev)
    setFormError(null)
  }

  const switchToPropose = () => {
    setMode('B')
    historyRef.current = ['browse']
    setDirection('right')
    setStepKey('propose-name')
    if (search) setProposeNom(search)
    setFormError(null)
  }

  const selectProductFromCatalog = (product) => {
    setSelectedProduct(product)
    setOfferUnit(null)
    goTo('canaux', 'right')
  }

  // --- Anti-doublon : depuis B1, basculer sur le parcours A avec ce produit ---
  const switchBackToCatalog = (product) => {
    setMode('A')
    historyRef.current = []
    setDirection('right')
    setSelectedProduct(product)
    setOfferUnit(null)
    setStepKey('canaux')
    setFormError(null)
  }

  // --- Fin de la proposition : elle devient LE produit du wizard (prototype) ---
  const terminerProposition = (unit) => {
    const cat = proposeCategory
    setSelectedProduct({
      proposition: true,
      nom: proposeNom.trim(),
      unit,
      emoji: '🆕',
      category: cat ? { id: cat.id, nom: cat.nom, emoji: cat.emoji, allowExclusive: cat.allowExclusive } : null,
      photo: proposePhoto,
    })
    setOfferUnit(unit)
    setMode('A')
    goTo('canaux', 'right')
  }

  // --- Submit final ---
  // Prix a STOCKER pour un canal, quel que soit le mode saisi (S77, ADR-072).
  const prixStockeDuCanal = (code) => {
    const module = catalogue.find((ch) => ch.code === code)?.module
    const taux = Number(module?.commissionDefaut ?? 10) / 100
    const pr = prixParCanal[code]
    const val = parseFloat((pr?.val || '').replace(',', '.'))
    if (isNaN(val) || val <= 0) return null
    // Valeur STOCKEE au canal (S77, ADR-072) : net au canal Paniers, prix de
    // vente aux Evenements.
    return calculerCanal(code, pr.mode, val, taux, tvaVenteDe(selectedProduct?.tvaCode))?.stocke ?? null
  }

  const submitOffer = async () => {
    const actifs = canauxChoisis(catalogue, canauxEff)
    if (!selectedProduct || actifs.length === 0) {
      setFormError('Choisissez au moins un canal de vente')
      return
    }
    // Canal principal = le premier tarife (prototype : Paniers avant Marche),
    // sinon le premier actif (Atelier seul, non tarife).
    const tarifes = canauxTarifes(catalogue, canauxEff)
    const principal = tarifes[0] || actifs[0]
    const prixPrincipal = tarifes.length ? prixStockeDuCanal(principal.code) : 0
    if (tarifes.length && prixPrincipal == null) {
      setFormError('Indiquez un prix pour chaque canal')
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      let productId = selectedProduct.id
      // Proposition : la reference part en validation, l'offre se cree dessus.
      // « Ma marque » devient une demande d'exclusivite si le rayon le permet.
      if (selectedProduct.proposition) {
        const produit = await api.producer.proposeProduct({
          nom: selectedProduct.nom,
          categoryId: selectedProduct.category?.id,
          unit: selectedProduct.unit,
          requestExclusive: exclusif && selectedProduct.category?.allowExclusive ? 'true' : undefined,
        }, selectedProduct.photo)
        productId = produit?.id
        if (!productId) throw new Error('La proposition n\'a pas pu être enregistrée')
      }
      const offre = await api.producer.createOffer({
        productId,
        moduleId: principal.module.id,
        prix: prixPrincipal ?? 0,
        prixUnit: offerUnit || selectedProduct.unit || 'kg',
        customName: exclusif && customName ? customName : undefined,
        brandName: exclusif && brandName ? brandName : undefined,
        stock: stock ? parseInt(stock, 10) : undefined,
      })
      // Les autres canaux, avec leur prix propre s'ils sont tarifes.
      for (const ch of actifs) {
        if (ch.code === principal.code) continue
        const prixCanal = tarifes.some((t) => t.code === ch.code) ? prixStockeDuCanal(ch.code) : undefined
        await api.producer.setOfferChannel(offre?.id, ch.code, true, prixCanal ?? undefined)
      }
      onSuccess?.(selectedProduct.proposition ? 'Offre créée — référence en validation Manrina.' : 'Offre créée !')
      onClose?.()
    } catch (err) {
      setFormError(err.message || 'Erreur à la création de l\'offre')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  // --- Configuration de chaque etape ---

  const stepConfig = {
    browse: {
      title: 'Quel produit ?',
      subtitle: null,
      content: (
        <StepBrowse
 tc={tc} ts={ts} cardBg={cardBg} inputBg={inputBg}
          search={search} onSearchChange={setSearch}
          categories={categories}
          categoryId={categoryId} onCategorySelect={setCategoryId}
          catalog={catalog} catLoading={catLoading}
          onSelectProduct={selectProductFromCatalog}
          onSwitchToPropose={switchToPropose}
        />
      ),
      cta: null,
    },
    canaux: {
      title: 'Où le vendre ?',
      subtitle: null,
      content: (
        <StepCanaux
          product={selectedProduct} catalogue={catalogue} canaux={canauxEff}
          onToggle={toggleCanal}
        />
      ),
      cta: {
        label: 'Continuer',
        enabled: canauxChoisis(catalogue, canauxEff).length > 0,
        onClick: () => goTo('price'),
      },
    },
    price: {
      title: 'Vos prix',
      subtitle: null,
      content: (
        <StepPrice
          product={selectedProduct} catalogue={catalogue} canaux={canauxEff}
          prix={prixParCanal}
          onPrixChange={(code, pr) => setPrixParCanal((p) => ({ ...p, [code]: pr }))}
          unite={offerUnit} onUniteChange={setOfferUnit}
        />
      ),
      cta: {
        label: 'Continuer',
        enabled: canauxTarifes(catalogue, canauxEff).every((c) => prixStockeDuCanal(c.code) != null),
        onClick: () => goTo('type'),
      },
    },
    type: {
      title: 'Type de produit',
      subtitle: null,
      content: (
        <StepTypeProduit
          product={selectedProduct}
          exclusif={exclusif} onExclusifChange={setExclusif}
          brandName={brandName} onBrandNameChange={setBrandName}
          customName={customName} onCustomNameChange={setCustomName}
        />
      ),
      cta: { label: 'Continuer', enabled: true, onClick: () => goTo('stock') },
    },
    stock: {
      title: 'Stock & récap',
      subtitle: null,
      content: (
        <StepStockRecap
          product={selectedProduct} catalogue={catalogue} canaux={canauxEff} exclusif={exclusif}
          customName={exclusif ? customName : ''}
          stock={stock} onStockChange={setStock}
          unite={offerUnit}
        />
      ),
      cta: { label: saving ? 'Création…' : 'Créer mon offre', enabled: !!stock && !saving, onClick: submitOffer, final: true },
    },
    // Sous-parcours « Proposer un produit » (prototype) : la question en
    // sous-titre, une decision par ecran, pas de progression.
    'propose-name': {
      title: 'Proposer un produit',
      subtitle: 'Comment s\'appelle votre produit ?',
      content: (
        <StepProposeName
          nom={proposeNom} onNomChange={setProposeNom}
          onSelectExisting={switchBackToCatalog}
        />
      ),
      cta: { label: 'Continuer', enabled: proposeNom.trim().length >= 2, onClick: () => goTo('propose-category') },
    },
    'propose-category': {
      title: 'Proposer un produit',
      subtitle: 'Dans quel rayon le classer ?',
      content: (
        <StepProposeCategory
          categories={categories}
          categoryId={proposeCategoryId}
          onSelect={(cat) => { setProposeCategoryId(cat.id); goTo('propose-photo') }}
        />
      ),
      cta: null,
    },
    'propose-photo': {
      title: 'Proposer un produit',
      subtitle: 'Une photo de votre produit',
      content: <StepProposePhoto photo={proposePhoto} onPhotoChange={setProposePhoto} />,
      cta: { label: 'Continuer', enabled: !!proposePhoto, onClick: () => goTo('propose-unite') },
    },
    'propose-unite': {
      title: 'Proposer un produit',
      subtitle: 'Vous le vendez…',
      content: <StepProposeUnite onChoisir={terminerProposition} />,
      cta: null,
    },
  }

  const current = stepConfig[stepKey] || stepConfig.browse

  // --- Footer commun (CTA gros en bas) ---
  // CTA du prototype : pilule VERT profond « Continuer », grisee tant que
  // l'ecran n'est pas complet ; ORANGE pour l'acte final « Creer mon offre ».
  // Plus de degrade orange->vert : il n'existe nulle part dans le prototype.
  const footer = current.cta ? (
    <button
      onClick={current.cta.onClick}
      disabled={!current.cta.enabled}
      className="w-full rounded-full text-[14px] font-extrabold active:scale-[0.98] transition-transform"
      style={current.cta.enabled
        ? (current.cta.final
            ? { padding: '15px 0', background: 'rgb(var(--c-primary))', color: '#fff', fontSize: 15, boxShadow: '0 10px 26px rgb(var(--c-primary) / 0.35)' }
            : { padding: '14px 0', background: 'rgb(var(--c-green))', color: 'rgb(var(--c-creme))' })
        : { padding: '14px 0', background: 'rgba(7,62,61,0.15)', color: 'rgba(247,240,234,0.9)' }}
    >
      {current.cta.label}
    </button>
  ) : null

  return (
    <WizardShell
 tc={tc} ts={ts}
      title={current.title}
      subtitle={current.subtitle}
      stepIndex={stepIndex}
      totalSteps={mode === 'B' ? 0 : steps.length}
      direction={direction}
      canBack={historyRef.current.length > 0}
      onBack={goBack}
      onClose={onClose}
      footer={
        <div className="space-y-2">
          {formError && (
            <div className={`p-2.5 rounded-xl text-xs ${isWeb3 ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-600'}`}>
              {formError}
            </div>
          )}
          {footer}
        </div>
      }
    >
      {current.content}
    </WizardShell>
  )
}

export default AddProductModal
